/**
 * A forma de uma ficha do Mistik Dream, e as curvas que ela obedece.
 *
 * <p><b>O vocabulário não é inventado aqui.</b> Os seis atributos e as perícias vêm de
 * `paginas/gamedesign`, onde o time os define — a ficha da mesa fala a mesma língua do
 * game design, e uma segunda lista divergiria no dia em que uma perícia mudasse de nome.
 *
 * <p><b>O que é derivado não é guardado.</b> O nível do personagem sai do XP, o nível de
 * uma perícia sai do XP dela, e os pontos restantes saem do que os atributos custaram.
 * Guardar qualquer um desses seria guardar o resultado da regra em vez da regra — e no
 * dia em que a curva mudasse, a ficha continuaria dizendo o número antigo.
 *
 * <p><b>O backend não conhece esta forma</b>: a ficha inteira viaja num `jsonb` opaco. O
 * jogo ainda está sendo desenhado, e cada campo novo viraria uma migration — que é
 * exatamente o que uma mesa experimental não pode pagar.
 */
import { ATRIBUTOS } from '../gamedesign/atributos';
import {
  COLETAS,
  COMBATE_DEFENSIVAS,
  COMBATE_OFENSIVAS,
  CRIACOES,
  OUTRAS,
} from '../gamedesign/pericias';
import type { Pericia } from '../gamedesign/pericias';

/** Uma barra da ficha: HP e SP. */
export interface Medida {
  atual: number;
  max: number;
}

export interface Ficha {
  /** Só o acumulado. O nível sai dele, por {@link nivelDoPersonagem}. */
  xp: number;
  hp: Medida;
  sp: Medida;
  /** A sigla aponta para o valor comprado com os pontos. Ver a nota acima do tipo. */
  atributos: Record<string, number>;
  /** Iniciativa, Acerto, Dano, Defesa, Esquiva, Bloqueio — texto livre. */
  combate: Record<string, string>;
  peso: Medida;
  condicao: string;
  /** Só o XP de cada perícia. O nível sai dele, por {@link nivelDaPericia}. */
  pericias: Record<string, number>;
  /** As linhas de inventário. Vazias contam: é onde a mesa escreve durante a sessão. */
  itens: string[];
}

// --------------------------------------------------------------- as curvas

/**
 * Quantos pontos o personagem tem para distribuir na criação.
 *
 * <p>Trinta e seis, e todos os atributos começam em <b>zero</b> — é o jogador que decide
 * onde eles vão. Um valor inicial qualquer seria uma escolha de build feita pela tela.
 */
export const PONTOS_DE_ATRIBUTO = 36;

/**
 * O que custa levar um atributo de {@code valor − 1} para {@code valor}.
 *
 * <p>Custa o número aonde se chega: de 0 para 1 custa 1, de 3 para 4 custa 4. É a mesma
 * regra do jogo, e é o que faz espalhar pontos ser barato e especializar ser caro.
 */
export const custoDoProximoPonto = (valorAtual: number) => valorAtual + 1;

/** O acumulado gasto para deixar um atributo em {@code valor}: 1+2+…+valor. */
export const gastoDoAtributo = (valor: number) => (valor * (valor + 1)) / 2;

export const pontosGastos = (atributos: Record<string, number>) =>
  Object.values(atributos).reduce((soma, v) => soma + gastoDoAtributo(v), 0);

export const pontosRestantes = (atributos: Record<string, number>) =>
  PONTOS_DE_ATRIBUTO - pontosGastos(atributos);

/**
 * O XP acumulado que o nível {@code n} exige para virar {@code n + 1}.
 *
 * <p>Cem vezes o triangular: 100 para sair do 1, 300 para sair do 2, 600 para sair do 3.
 * O número mostrado é o <b>total</b>, e não o que falta — foi assim que a curva foi
 * descrita ("ficando 300 total"), e é o que a ficha do jogo mostra.
 */
export const xpDoProximoNivel = (nivel: number) => (100 * (nivel * (nivel + 1))) / 2;

/**
 * O nível que este XP compra.
 *
 * <p>Por laço, e não por fórmula fechada: a curva é de design e vai mudar, e um laço que
 * consome a mesma função de custo continua certo depois da mudança. A raiz quadrada que
 * inverte o triangular teria de ser reescrita junto — e sem sintoma nenhum se ninguém
 * reescrevesse.
 */
export function nivelDoPersonagem(xp: number): number {
  let nivel = 1;
  while (xp >= xpDoProximoNivel(nivel)) nivel++;
  return nivel;
}

/**
 * O XP acumulado que leva uma perícia ao nível {@code n}.
 *
 * <p>O triangular puro: 1 para o nível 1, 3 para o 2, 6 para o 3. Mesma lógica dos
 * pontos de atributo, em escala menor — subir custa o número aonde se chega.
 */
export const xpDaPericiaNoNivel = (nivel: number) => (nivel * (nivel + 1)) / 2;

/** Toda perícia começa no nível <b>0</b>. */
export function nivelDaPericia(xp: number): number {
  let nivel = 0;
  while (xp >= xpDaPericiaNoNivel(nivel + 1)) nivel++;
  return nivel;
}

/** O acumulado que a perícia precisa alcançar para subir do nível em que está. */
export const xpParaSubirPericia = (xp: number) => xpDaPericiaNoNivel(nivelDaPericia(xp) + 1);

// ------------------------------------------------------------- os catálogos

/** Os blocos de perícia, na ordem e com os nomes que a ficha do jogo usa. */
export const GRUPOS_DE_PERICIA: { titulo: string; pericias: Pericia[] }[] = [
  { titulo: 'Ofensivas', pericias: COMBATE_OFENSIVAS },
  { titulo: 'Defensivas', pericias: COMBATE_DEFENSIVAS },
  { titulo: 'Coleta', pericias: COLETAS },
  { titulo: 'Craft', pericias: CRIACOES },
  /*
   * Por último, e de propósito: as quatro de OUTRAS não são de combate nem produzem
   * nada — são o corpo na estrada (Viajar) e as subações do Descansar. A mesa as pediu
   * (Theo, 19/08/2026), e nenhuma tela de Game Design as mostra ainda.
   */
  { titulo: 'Outras', pericias: OUTRAS },
];

/**
 * Toda perícia que a ficha da mesa conhece — é daqui que o mapa de XP nasce.
 *
 * <p>Derivada dos grupos, e não de `TODAS_AS_PERICIAS`: aquela lista é "todas, como a
 * Ficha [de Game Design] as apresenta", e as de OUTRAS não estão nela. Duas listas que
 * quase coincidem é armadilha — esta aqui é, por construção, exatamente o que a tela
 * desenha.
 */
const PERICIAS_DA_FICHA: Pericia[] = GRUPOS_DE_PERICIA.flatMap((g) => g.pericias);

/** As seis medidas de combate, na ordem em que a ficha do jogo as mostra. */
export const CAMPOS_DE_COMBATE: { id: string; rotulo: string; dica: string }[] = [
  { id: 'iniciativa', rotulo: 'Iniciativa', dica: '9' },
  { id: 'acerto', rotulo: 'Acerto', dica: '5 / 4 / 4' },
  { id: 'dano', rotulo: 'Dano', dica: '6 / 5 / 4' },
  { id: 'defesa', rotulo: 'Defesa', dica: '0' },
  { id: 'esquiva', rotulo: 'Esquiva', dica: '10' },
  { id: 'bloqueio', rotulo: 'Bloqueio', dica: '11' },
];

/** Quantas linhas de inventário a ficha nasce oferecendo. */
export const LINHAS_DE_ITEM = 10;

export const medida = (atual: number, max: number): Medida => ({ atual, max });

/**
 * Uma ficha nova: nível 1, sem XP, atributos em zero e os 36 pontos por gastar.
 *
 * <p>HP e SP nascem com um valor jogável porque não há regra de derivação fechada ainda
 * — quando houver, eles saem daqui e viram derivados, como o nível já é.
 */
export const fichaEmBranco = (): Ficha => ({
  xp: 0,
  hp: medida(25, 25),
  sp: medida(11, 11),
  atributos: Object.fromEntries(ATRIBUTOS.map((a) => [a.sigla, 0])),
  combate: Object.fromEntries(CAMPOS_DE_COMBATE.map((c) => [c.id, ''])),
  peso: medida(0, 15),
  condicao: 'Normal',
  pericias: Object.fromEntries(PERICIAS_DA_FICHA.map((p) => [p.id, 0])),
  itens: Array(LINHAS_DE_ITEM).fill(''),
});

// ------------------------------------------------------- o que chega guardado

const numero = (valor: unknown, padrao: number): number =>
  typeof valor === 'number' && Number.isFinite(valor) ? valor : padrao;

const texto = (valor: unknown, padrao = ''): string =>
  typeof valor === 'string' ? valor : padrao;

const lerMedida = (valor: unknown, padrao: Medida): Medida => {
  const m = (valor ?? {}) as Record<string, unknown>;
  return { atual: numero(m.atual, padrao.atual), max: numero(m.max, padrao.max) };
};

/**
 * Reconstrói a ficha sobre a de fábrica, campo a campo.
 *
 * <p>Reconstruir é o que faz uma perícia nova não quebrar a ficha que já foi preenchida,
 * e uma que saia da lista não ficar assombrando o que se lê — foi exatamente o que
 * aconteceu quando Duas Mãos e Arma Exótica deram lugar a Magia Ofensiva.
 */
export function interpretarFicha(guardado: unknown): Ficha {
  const base = fichaEmBranco();
  if (!guardado || typeof guardado !== 'object') return base;
  const lido = guardado as Record<string, unknown>;

  const atributos = (lido.atributos ?? {}) as Record<string, unknown>;
  const pericias = (lido.pericias ?? {}) as Record<string, unknown>;
  const combate = (lido.combate ?? {}) as Record<string, unknown>;

  return {
    xp: numero(lido.xp, 0),
    hp: lerMedida(lido.hp, base.hp),
    sp: lerMedida(lido.sp, base.sp),
    /*
     * O atributo era `{base, bonus}` e passou a ser um número: o bônus de equipamento
     * saiu da ficha. De uma gravação antiga aproveita-se o `base`, que é o único dos dois
     * que os pontos compraram — o bônus não tem para onde ir.
     */
    atributos: Object.fromEntries(
      ATRIBUTOS.map((a) => [a.sigla, lerValorDeAtributo(atributos[a.sigla])])
    ),
    combate: Object.fromEntries(CAMPOS_DE_COMBATE.map((c) => [c.id, texto(combate[c.id])])),
    peso: lerMedida(lido.peso, base.peso),
    condicao: texto(lido.condicao, base.condicao),
    /*
     * A perícia guardava `{nivel, xp, xpMax}` e passou a guardar só o XP. Uma ficha
     * gravada antes disso traz o objeto — dele aproveita-se o XP, que é o único número
     * que continua sendo dado; nível e teto voltaram a ser conta.
     */
    pericias: Object.fromEntries(
      PERICIAS_DA_FICHA.map((p) => [p.id, lerXpDePericia(pericias[p.id])])
    ),
    itens: normalizarItens(lido.itens),
  };
}

function lerXpDePericia(guardado: unknown): number {
  if (typeof guardado === 'number') return Number.isFinite(guardado) ? guardado : 0;
  if (guardado && typeof guardado === 'object') {
    return numero((guardado as Record<string, unknown>).xp, 0);
  }
  return 0;
}

function normalizarItens(guardado: unknown): string[] {
  const lidos = Array.isArray(guardado) ? guardado.map((i) => texto(i)) : [];
  const faltam = Math.max(0, LINHAS_DE_ITEM - lidos.length);
  return [...lidos, ...Array(faltam).fill('')];
}


/** Quanto da barra está cheio, entre 0 e 1. Máximo zero desenha barra vazia, não NaN. */
export const fracao = (m: Medida) => (m.max > 0 ? Math.min(1, Math.max(0, m.atual / m.max)) : 0);

/**
 * O valor de um atributo, vindo do que estava gravado.
 *
 * <p>Aceita o número de hoje e o `{base, bonus}` de ontem — o bônus de equipamento saiu
 * da ficha, e de uma gravação antiga só o `base` tem para onde ir. Sem esta ponte, toda
 * ficha já criada voltaria com os seis atributos em zero, e ninguém associaria a perda a
 * uma mudança de formato.
 */
function lerValorDeAtributo(guardado: unknown): number {
  if (typeof guardado === 'number') return Number.isFinite(guardado) ? guardado : 0;
  if (guardado && typeof guardado === 'object') {
    return numero((guardado as Record<string, unknown>).base, 0);
  }
  return 0;
}
